import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Package, Truck, CheckCircle, Clock } from 'lucide-react';
import { useCatalogData } from '../hooks/useCatalogData';
import { fetchOrders } from '../services/orderService';

const Orders = () => {
  const { products: catalogProducts = [] } = useCatalogData();
  const [orders, setOrders] = useState(() => []);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const products = useMemo(
    () => (Array.isArray(catalogProducts) && catalogProducts.length > 0 ? catalogProducts : []),
    [catalogProducts],
  );

  const productIndex = useMemo(() => {
    const index = new Map();
    products.forEach((product) => {
      if (!product) return;
      const keys = [
        product.id,
        product.productId,
        product.sku,
        product.raw?.productId,
        product.raw?.sku,
        product.raw?.id,
      ].filter(Boolean);
      keys.forEach((key) => {
        const normalized = String(key).toLowerCase();
        if (!index.has(normalized)) {
          index.set(normalized, product);
        }
      });
    });
    return index;
  }, [products]);

  const toNumber = (value) => {
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    if (typeof value === 'string') {
      const cleaned = value.replace(/[^0-9.-]+/g, '');
      const parsed = Number.parseFloat(cleaned);
      if (!Number.isNaN(parsed)) return parsed;
    }
    return 0;
  };

  const resolvePrice = (...candidates) => {
    for (const candidate of candidates) {
      const numeric = toNumber(candidate);
      if (numeric > 0) {
        return numeric;
      }
    }
    return 0;
  };

  const getResolvedProduct = (productId) => {
    if (!productId) return null;
    const normalized = String(productId).toLowerCase();
    return productIndex.get(normalized) ?? null;
  };

  const resolveLineItemPrice = (item, product) => {
    const productRaw = product?.raw ?? {};
    return resolvePrice(
      item?.price,
      item?.amount,
      item?.total,
      product?.price,
      productRaw.promo_price,
      productRaw.sale_price,
      productRaw.mrp_price,
      productRaw.cost_price,
      productRaw.price,
    );
  };

  const resolveOrderTotal = (order) => {
    if (!order?.items || order.items.length === 0) {
      return resolvePrice(order?.total, order?.amount);
    }
    const derivedTotal = order.items.reduce((sum, item) => {
      const product = getResolvedProduct(item.productId);
      const linePrice = resolveLineItemPrice(item, product);
      const quantity = toNumber(item?.quantity ?? item?.qty ?? 1) || 1;
      return sum + linePrice * quantity;
    }, 0);

    return derivedTotal > 0 ? derivedTotal : resolvePrice(order?.total, order?.amount);
  };

  useEffect(() => {
    let cancelled = false;

    const loadOrders = async () => {
      try {
        setLoading(true);
        const data = await fetchOrders();
        if (cancelled) return;
        if (Array.isArray(data) && data.length > 0) {
          setOrders(data);
        } else {
          setOrders([]);
        }
        setError(null);
      } catch (err) {
        if (!cancelled) {
          setOrders([]);
          setError(err);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadOrders();

    return () => {
      cancelled = true;
    };
  }, []);

  const getStatusIcon = (status) => {
    const normalized = (status ?? '').toLowerCase();
    switch (normalized) {
      case 'delivered':
        return <CheckCircle className="text-green-600" size={24} />;
      case 'shipped':
        return <Truck className="text-blue-600" size={24} />;
      case 'processing':
        return <Clock className="text-yellow-600" size={24} />;
      case 'awaiting shipment':
      case 'pending':
        return <Clock className="text-yellow-600" size={24} />;
      default:
        return <Package className="text-gray-600" size={24} />;
    }
  };

  const getStatusColor = (status) => {
    const normalized = (status ?? '').toLowerCase();
    switch (normalized) {
      case 'delivered':
        return 'bg-green-100 text-green-800';
      case 'shipped':
        return 'bg-blue-100 text-blue-800';
      case 'processing':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-8">My Orders</h1>

        {loading && (!orders || orders.length === 0) ? (
          <div className="bg-white rounded-lg shadow p-12 text-center">
            <Package size={64} className="mx-auto text-gray-300 mb-4 animate-pulse" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Loading your orders...</h2>
            <p className="text-gray-600">Please wait while we retrieve your recent orders.</p>
          </div>
        ) : orders.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-12 text-center">
            <Package size={64} className="mx-auto text-gray-400 mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">No Orders Yet</h2>
            <p className="text-gray-600 mb-6">Start shopping to see your orders here!</p>
            <Link
              to="/products"
              className="inline-block bg-red-600 text-white px-8 py-3 rounded hover:bg-red-700 transition"
            >
              Start Shopping
            </Link>
            {error && (
              <p className="mt-4 text-sm text-gray-500">
                We were unable to load your live order history. Showing fallback data instead.
              </p>
            )}
          </div>
        ) : (
          <div className="space-y-6">
            {orders.map((order) => (
              <div key={order.id} className="bg-white rounded-lg shadow overflow-hidden">
                {/* Order Header */}
                <div className="bg-gray-50 px-6 py-4 border-b">
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between">
                    <div className="flex items-center space-x-4 mb-4 md:mb-0">
                      {getStatusIcon(order.status)}
                      <div>
                        <h3 className="font-bold text-gray-900">Order #{order.id}</h3>
                        <p className="text-sm text-gray-600">Placed on {new Date(order.date).toLocaleDateString()}</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-4">
                      <span className={`px-4 py-2 rounded-full text-sm font-semibold ${getStatusColor(order.status)}`}>
                        {order.status
                          ? order.status.charAt(0).toUpperCase() + order.status.slice(1)
                          : 'Unknown'}
                      </span>
                      <span className="text-xl font-bold text-gray-900">
                        {(() => {
                          const derivedTotal = resolveOrderTotal(order);
                          return derivedTotal > 0
                            ? `$${derivedTotal.toFixed(2)}`
                            : order.total ?? '$0.00';
                        })()}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Order Items */}
                <div className="p-6">
                  {order.items && order.items.length > 0 ? (
                    <div className="space-y-4 mb-6">
                      {order.items.map((item, index) => {
                        const product = getResolvedProduct(item.productId);
                        const linePrice = resolveLineItemPrice(item, product);
                        const quantity = toNumber(item?.quantity ?? item?.qty ?? 1) || 1;
                        return (
                          <div key={item.productId ?? `order-item-${index}`} className="flex items-center space-x-4">
                            <img
                              src={product?.image || '/placeholder.jpg'}
                              alt={product?.name ?? 'Product'}
                              onError={(e) => {
                                e.target.src = '/placeholder.jpg';
                              }}
                              className="w-20 h-20 object-cover rounded"
                            />
                            <div className="flex-1">
                              {product ? (
                                <Link
                                  to={`/product/${product.slug}`}
                                  className="font-semibold text-gray-900 hover:text-red-600 transition"
                                >
                                  {product.name}
                                </Link>
                              ) : (
                                <span className="font-semibold text-gray-900">Product</span>
                              )}
                              <p className="text-sm text-gray-500">Quantity: {item.quantity ?? 1}</p>
                            </div>
                            <div className="text-right">
                              <p className="font-bold text-gray-900">
                                {linePrice > 0 ? `$${linePrice.toFixed(2)}` : '$0.00'}
                              </p>
                              <p className="text-sm text-gray-500">each</p>
                              {quantity > 1 && (
                                <p className="text-xs text-gray-400">
                                  Line total: ${(linePrice * quantity).toFixed(2)}
                                </p>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="mb-6 text-sm text-gray-500">
                      Order line items are not available for this entry.
                    </div>
                  )}

                  {/* Shipping Info */}
                  <div className="border-t pt-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-2">Shipping Address</h4>
                        <p className="text-sm text-gray-600">{order.shippingAddress || 'Not provided'}</p>
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-2">Delivery Information</h4>
                        {order.trackingNumber && (
                          <p className="text-sm text-gray-600 mb-1">
                            Tracking: <span className="font-semibold">{order.trackingNumber}</span>
                          </p>
                        )}
                        <p className="text-sm text-gray-600">
                          {order.estimatedDelivery
                            ? `Estimated Delivery: ${new Date(order.estimatedDelivery).toLocaleDateString()}`
                            : 'Estimated delivery information unavailable.'}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex flex-wrap gap-3 mt-6">
                    {order.trackingNumber && (
                      <button className="flex-1 md:flex-none border border-gray-300 px-6 py-2 rounded hover:bg-gray-50 transition font-semibold">
                        Track Order
                      </button>
                    )}
                    <button className="flex-1 md:flex-none border border-gray-300 px-6 py-2 rounded hover:bg-gray-50 transition font-semibold">
                      View Invoice
                    </button>
                    {order.status === 'delivered' && (
                      <button className="flex-1 md:flex-none bg-red-600 text-white px-6 py-2 rounded hover:bg-red-700 transition font-semibold">
                        Reorder
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Orders;
